import { useEffect, useRef, useState } from "react";
import { NavLink, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { Icon } from "./components/Icon";
import { Avatar, Btn } from "./components/kit";
import { NAV, CRUMB, TOP_ACTION } from "./data/nav";
import { useMe, isAuthed, login } from "./lib/api";
import { useNotifications } from "./lib/hooks";
import { capturePageview } from "./lib/analytics";
import { Placeholder } from "./pages/Placeholder";
import { DashboardPage } from "./pages/learn/Dashboard";
import { CurriculumPage } from "./pages/learn/Curriculum";
import { AchievementsPage } from "./pages/learn/Achievements";
import { ProfilePage } from "./pages/context/Profile";
import { HistoryPage } from "./pages/context/History";
import { BillingPage } from "./pages/account/Billing";
import { SettingsPage } from "./pages/account/Settings";
import { TeamPage } from "./pages/account/Team";
import { HelpPage } from "./pages/Help";

import type { ReactElement } from "react";

// Real pages, keyed by nav id. Unmapped ids fall back to the Placeholder.
const PAGES: Record<string, ReactElement> = {
  dashboard: <DashboardPage />,
  curriculum: <CurriculumPage />,
  achievements: <AchievementsPage />,
  profile: <ProfilePage />,
  history: <HistoryPage />,
  billing: <BillingPage />,
  settings: <SettingsPage />,
  team: <TeamPage />,
  help: <HelpPage />,
};

function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  const me = useMe();
  const wsName = me?.org?.name ?? "Your workspace";
  return (
    <aside className="side">
      <div className="side-top">
        <button className="brand">
          <span className="bmark"><span className="bdot" /></span>
          <span className="bn">Adaptr Orbit<small>{wsName}</small></span>
          <Icon name="chevrons-up-down" size={15} color="var(--fg-3)" className="brand-chev" />
        </button>
      </div>
      <button className="search-side">
        <Icon name="search" size={15} />
        <span>Search</span>
        <span className="kbd">⌘K</span>
      </button>
      <nav className="nav">
        {NAV.map((grp) => (
          <div key={grp.group}>
            <div className="nav-h">{grp.group}</div>
            {grp.items.map((it) => (
              <NavLink
                key={it.id}
                to={`/${it.id}`}
                className={({ isActive }) => `nav-i ${isActive ? "on" : ""}`}
                onClick={onNavigate}
              >
                <Icon name={it.icon} size={17} className="nav-ic" />
                <span className="nt">{it.label}</span>
                {it.count != null && <span className="cnt">{it.count}</span>}
                {it.badge && <span className="np">{it.badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="side-foot">
        {me ? (
          <button className="user-chip">
            <Avatar initials={me.initials} size={32} />
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div className="un">{me.name}</div>
              <div className="um">{me.plan + " · " + me.email}</div>
            </div>
            <Icon name="chevrons-up-down" size={15} color="var(--fg-3)" />
          </button>
        ) : (
          <button className="user-chip" onClick={login}>
            <span className="avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
              <Icon name="user" size={16} />
            </span>
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div className="un">{isAuthed() ? "Loading…" : "Sign in"}</div>
              <div className="um">{isAuthed() ? "Fetching your account" : "Access your Control Room"}</div>
            </div>
            <Icon name="arrow-up-right" size={15} color="var(--fg-3)" />
          </button>
        )}
        <a className="get-app" href="https://downloads.adaptr.ai" target="_blank" rel="noreferrer">
          <Icon name="panel-top" size={15} className="ga-ic" />
          <span>Get the desktop app</span>
          <Icon name="arrow-up-right" size={14} className="ga-x" />
        </a>
      </div>
    </aside>
  );
}

function Topbar({ onMenu }: { onMenu: () => void }) {
  const { pathname } = useLocation();
  const route = pathname.replace(/^\//, "") || "dashboard";
  const crumb = CRUMB[route] || ["", ""];
  const act = TOP_ACTION[route];
  return (
    <header className="topbar">
      <button className="menu-btn" aria-label="Open menu" onClick={onMenu}>
        <Icon name="menu" size={20} />
      </button>
      <div className="crumb">
        <span className="crumb-group">{crumb[0]}</span>
        <Icon name="chevron-right" size={15} className="sl" />
        <b>{crumb[1]}</b>
      </div>
      <div className="tb-right">
        <NotificationBell />
        <button className="tb-btn"><Icon name="life-buoy" size={18} /></button>
        <div className="tb-div" />
        {act && <Btn variant={act.variant} icon={act.icon}>{act.label}</Btn>}
      </div>
    </header>
  );
}

const NOTIF_ICON: Record<string, string> = {
  broadcast: "megaphone", lesson_reminder: "graduation-cap", usage_alert: "zap", weekly_digest: "mail", system: "bell",
};

function NotificationBell() {
  const { items, unread, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  if (!isAuthed()) return <button className="tb-btn"><Icon name="bell" size={18} /></button>;
  return (
    <div className="notif-wrap" ref={ref}>
      <button className="tb-btn" aria-label="Notifications" onClick={() => setOpen((o) => !o)}>
        <Icon name="bell" size={18} />
        {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel">
          <div className="notif-head">
            <b>Notifications</b>
            {unread > 0 && <button className="notif-allread" onClick={() => markRead()}>Mark all read</button>}
          </div>
          <div className="notif-list">
            {items === null ? (
              <div className="notif-empty">Loading…</div>
            ) : items.length === 0 ? (
              <div className="notif-empty"><Icon name="bell" size={20} /><span>You're all caught up.</span></div>
            ) : (
              items.map((n) => {
                const Body = (
                  <>
                    <span className="notif-ic"><Icon name={NOTIF_ICON[n.type] || "bell"} size={15} /></span>
                    <div className="notif-body">
                      <div className="notif-t">{n.title}</div>
                      {n.body && <div className="notif-d">{n.body}</div>}
                      <div className="notif-ago">{n.ago}</div>
                    </div>
                    {!n.read && <span className="notif-unread" />}
                  </>
                );
                return n.link ? (
                  <a key={n.id} href={n.link} target="_blank" rel="noreferrer" className={`notif-row ${n.read ? "" : "fresh"}`} onClick={() => markRead(n.id)}>{Body}</a>
                ) : (
                  <button key={n.id} className={`notif-row ${n.read ? "" : "fresh"}`} onClick={() => markRead(n.id)}>{Body}</button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile drawer on navigation and lock body scroll while it's open.
  useEffect(() => setMenuOpen(false), [pathname]);
  // Anonymous pageview on every route change (no-op unless the user opted in).
  useEffect(() => { capturePageview(pathname); }, [pathname]);
  useEffect(() => {
    document.body.classList.toggle("no-scroll", menuOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [menuOpen]);

  return (
    <div className={`app ${menuOpen ? "menu-open" : ""}`}>
      <Sidebar onNavigate={() => setMenuOpen(false)} />
      {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} />}
      <main className="main">
        <Topbar onMenu={() => setMenuOpen(true)} />
        <div className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/lessons" element={<Navigate to="/curriculum" replace />} />
            {NAV.flatMap((g) => g.items).map((it) => (
              <Route key={it.id} path={`/${it.id}`} element={PAGES[it.id] ?? <Placeholder id={it.id} label={it.label} />} />
            ))}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
